/**
 * Lazy loading wrapper with automatic retry on chunk load failures
 * 
 * When a new deployment happens, old chunk files may no longer exist.
 * This wrapper catches ChunkLoadError and automatically reloads the page
 * to get the fresh bundle with correct chunk references.
 */
import React from 'react';

// Track if we've already attempted a reload to prevent infinite loops
const RELOAD_KEY = 'chunk_reload_attempted';
const RELOAD_EXPIRY = 10000; // 10 seconds

/**
 * Check if we recently attempted a reload
 */
function hasRecentReloadAttempt() {
  try {
    const reloadData = sessionStorage.getItem(RELOAD_KEY);
    if (!reloadData) return false;
    
    const { timestamp } = JSON.parse(reloadData);
    const elapsed = Date.now() - timestamp;
    
    // If less than 10 seconds since last reload attempt, don't reload again
    return elapsed < RELOAD_EXPIRY;
  } catch {
    return false;
  }
}

/**
 * Mark that we're attempting a reload
 */
function markReloadAttempt() {
  try {
    sessionStorage.setItem(RELOAD_KEY, JSON.stringify({
      timestamp: Date.now()
    }));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear the reload attempt marker (called on successful load)
 */
function clearReloadAttempt() {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Wrap React.lazy with automatic retry on chunk load failures
 * 
 * @param {Function} importFunc - The dynamic import function, e.g., () => import('./Component')
 * @param {string} componentName - Name of the component for logging
 * @returns {React.LazyExoticComponent} - Lazy component with retry logic
 */
export function lazyWithRetry(importFunc, componentName = 'Unknown') {
  return React.lazy(() => 
    importFunc()
      .then(module => {
        // Success - clear any reload markers
        clearReloadAttempt();
        return module;
      })
      .catch(error => {
        // Check if this is a chunk load error
        const isChunkError = 
          error.name === 'ChunkLoadError' ||
          error.message?.includes('Loading chunk') ||
          error.message?.includes('Failed to fetch dynamically imported module');
        
        if (isChunkError) {
          console.warn(`⚠️ Chunk load failed for ${componentName}:`, error.message);
          
          // Check if we've already tried reloading recently
          if (hasRecentReloadAttempt()) {
            console.error(`❌ Chunk reload already attempted for ${componentName}, not retrying to prevent loop`);
            // Return a fallback error component instead of throwing
            return {
              default: () => (
                <div style={{ 
                  padding: '20px', 
                  textAlign: 'center',
                  backgroundColor: '#fef2f2',
                  border: '1px solid #fecaca',
                  borderRadius: '8px',
                  margin: '20px'
                }}>
                  <h3 style={{ color: '#dc2626', marginBottom: '10px' }}>
                    Failed to load component
                  </h3>
                  <p style={{ color: '#7f1d1d', marginBottom: '15px' }}>
                    There was a problem loading this page. Please try:
                  </p>
                  <button
                    onClick={() => {
                      clearReloadAttempt();
                      window.location.reload();
                    }}
                    style={{
                      backgroundColor: '#2563eb',
                      color: 'white',
                      padding: '10px 20px',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Reload Page
                  </button>
                </div>
              )
            };
          }
          
          // Mark that we're attempting a reload
          markReloadAttempt();
          
          console.log(`🔄 New deployment detected - reloading page to get fresh chunks...`);
          
          // Force reload to get fresh chunks
          window.location.reload();
          
          // Return a never-resolving promise since we're reloading
          return new Promise(() => {});
        }
        
        // For non-chunk errors, re-throw
        throw error;
      })
  );
}

export default lazyWithRetry;

